import { Modal, Spin, Table } from "antd";
import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { Locale } from "../../../utils";
import {
  handleMutationError,
  showNotificationWithIcon,
} from "../../../utils/helpers/helpers";
import {
  getList,
  remove,
  create,
  update,
} from "../../../utils/http/dataProvider";
import { getUserInfo } from "../../../utils/redux/reducers/User";
import { BreadCrumb } from "../../../components/main/LayoutComponents";

import "./VideoCategoryList.scss";
import { videoCategoryListColumns } from "./videoCategoryListColumns";
import VideoCategoryListHeader from "./VideoCategoryListHeader";
import AddOrEditVideoCategory from "./AddOrEditVideoCategory";

const i18n = Locale.getInstance();

const VideoCategoryList = () => {
  const user = useSelector((state) => getUserInfo(state));
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState();
  const [perPage, setPerPage] = useState(20);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const categoryFormRef = useRef();

  const selectCategory = (item) => {
    setCategory(item);
    const values = {
      zh_name: item?.zh_name ?? "",
      ug_name: item?.ug_name ?? "",
      order: item?.order ?? 0,
    };

    setTimeout(() => {
      categoryFormRef.current?.setFieldsValue(values);
    }, 100);

    setOpenEditModal(true);
  };

  const handleSave = () => {
    const values = categoryFormRef.current.getFieldsValue();
    const payload = {
      ug_name: values.ug_name?.trim(),
      zh_name: values.zh_name?.trim(),
      order:
        values.order === undefined || values.order === null
          ? 0
          : Number(values.order),
    };

    setLoading(true);
    const params = {
      data: payload,
      token: user.token,
    };

    if (category) {
      updateCategoryMutation.mutate({
        resource: "video-category",
        params: { ...params, id: category.id },
      });
    } else {
      addCategoryMutation.mutate({ resource: "video-category", params });
    }
  };

  const addCategoryMutation = useMutation(create, {
    onSuccess: () => {
      queryClient.invalidateQueries("video-categories");
      showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      setLoading(false);
      setOpenEditModal(false);
      setCategory(undefined);
      categoryFormRef.current?.resetFields();
    },
    onError: (error) => {
      handleMutationError(error);
      setOpenEditModal(false);
      setLoading(false);
    },
  });

  const updateCategoryMutation = useMutation(update, {
    onSuccess: () => {
      queryClient.invalidateQueries("video-categories");
      showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      setOpenEditModal(false);
      setLoading(false);
      setCategory(undefined);
      categoryFormRef.current?.resetFields();
    },
    onError: (error) => {
      handleMutationError(error);
      setOpenEditModal(false);
      setLoading(false);
    },
  });

  const { data, error, isLoading } = useQuery(
    ["video-categories", user.token],
    () =>
      getList("video-category", {
        pagination: {
          perPage: 2000,
          page: 1,
        },
        sort: {
          field: "order",
          order: "desc",
        },
        token: user.token,
      })
  );

  if (error) {
    showNotificationWithIcon(error.data?.message || error.message);
  }

  const deleteMutation = useMutation(remove, {
    onSuccess: () => {
      queryClient.invalidateQueries("video-categories");
      setLoading(false);
    },
    onError: (err) => {
      setLoading(false);
      handleMutationError(err);
    },
  });

  const breadCrumbData = [
    {
      href: "/main/videos",
      title: i18n.t("video.title"),
    },
    {
      title: i18n.t("video.category.title"),
    },
  ];

  return (
    <>
      <BreadCrumb data={breadCrumbData} />
      <Spin spinning={isLoading}>
        <div className="video-category-list">
          <VideoCategoryListHeader selectCategory={selectCategory} />
          <Table
            className="list"
            rowKey="id"
            columns={videoCategoryListColumns(
              deleteMutation,
              user.token,
              setLoading,
              selectCategory
            )}
            dataSource={data?.data}
            scroll={{ y: 600 }}
            pagination={{
              current: page,
              total: data?.total,
              pageSize: perPage,
              onChange: (p) => setPage(p),
              onShowSizeChange: (_p, size) => {
                setPerPage(size);
                setPage(1);
              },
              showSizeChanger: true,
            }}
          />
        </div>
      </Spin>
      <Modal
        title={
          category
            ? i18n.t("video.category.edit")
            : i18n.t("video.category.add")
        }
        open={openEditModal}
        onOk={handleSave}
        onCancel={() => {
          setOpenEditModal(false);
          setCategory(undefined);
          categoryFormRef.current?.resetFields();
        }}
        okText={i18n.t("base.modal.okText")}
        cancelText={i18n.t("base.modal.cancelText")}
        width={640}
        destroyOnClose
      >
        <AddOrEditVideoCategory ref={categoryFormRef} loading={loading} />
      </Modal>
    </>
  );
};

export default VideoCategoryList;
